import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetStockInOrder,
  useRecordStockInOrderPayment,
  useUpdateStockInOrderPaymentDetails,
  useUpdateStockInOrderHeader,
  useCreateStockInOrderRefundLine,
  useUpdateStockInOrderRefundLine,
  useUpdateStockInOrderCartLineStatus,
  useAddStockInOrderCartLine,
  useUpdateStockInOrderCartLine,
  useDeleteStockInOrderCartLine,
  useCancelStockInOrder,
  useCloseStockInOrder,
  getGetStockInOrderQueryKey,
  getListStockInOrdersQueryKey,
  getListStockInQueryKey,
  StockInOrderStatus,
  StockInOrderRefundTrigger,
  UpdateOrderRefundLineBodyStatus,
  type StockInOrder,
  type StockInOrderRefundLine,
  type StockInOrderCartLine,
  type StockInOrderCartLineStatus,
} from "@workspace/api-client-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { OrderProofUploader } from "./stock-in-order-form";
import { ItemCombobox } from "./item-combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, PackageCheck, PackageX, X, Plus, Upload, Eye, Pencil, Trash2 } from "lucide-react";
import { uploadPaymentProof, type UploadResult } from "@/lib/upload";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { ordId, stkInId } from "@/lib/display-id";
import { StockInOrderDeliveryForm } from "./stock-in-order-delivery-form";

// Phase 22-06 — Order detail drawer (Sheet) with 6 sections + status-driven actions.
//   1. Order header: ORD number, vendor, status, expected delivery window, notes.
//   2. Payment: prepaid amount + payment details; Draft → inline Record Payment form.
//   3. Cart lines table: item | ordered | delivered | remaining.
//   4. Linked delivery receipts: STK-IN | date | invoice | total.
//   5. Refund lines: trigger | amount | status | Mark Received | Add Refund.
//   6. Reconciliation summary: prepaid | delivered value | refunds received | balance.
//
// Status-driven action buttons:
//   Draft             → Record Payment
//   Placed / Partial  → Record Delivery + Cancel
//   Fully_Delivered   → Close Order (shortfall dialog if undelivered value)
//   Closed / Cancelled → view-only

const fmtINR = (n: number | null | undefined): string =>
  n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

type OrderStatus = (typeof StockInOrderStatus)[keyof typeof StockInOrderStatus];

const ORDER_STATUS_PILL: Record<OrderStatus, string> = {
  Draft: "bg-muted text-muted-foreground",
  Placed: "bg-blue-100 text-blue-700",
  Partially_Delivered: "bg-amber-100 text-amber-700",
  Fully_Delivered: "bg-green-100 text-green-700",
  Closed: "bg-slate-100 text-slate-600",
  Cancelled: "bg-red-100 text-red-600",
};

function StatusBadge({ status }: { status: OrderStatus }) {
  return (
    <span
      className={cn(
        "inline-flex rounded px-2 py-0.5 text-xs font-medium",
        ORDER_STATUS_PILL[status] ?? "bg-muted text-muted-foreground",
      )}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

// R3 — cart-line status palette + dropdown options.
const CART_LINE_STATUS_OPTIONS: StockInOrderCartLineStatus[] = [
  "Ordered",
  "In_Progress",
  "Delivered",
  "Returned",
  "Refunded",
  "Personal",
];

function cartLineStatusPill(s: StockInOrderCartLineStatus): string {
  switch (s) {
    case "Ordered":     return "bg-muted text-muted-foreground";
    case "In_Progress": return "bg-blue-50 text-blue-700";
    case "Delivered":   return "bg-green-50 text-green-700";
    case "Returned":    return "bg-amber-50 text-amber-700";
    case "Refunded":    return "bg-red-50 text-red-700";
    case "Personal":    return "bg-purple-50 text-purple-700";
  }
}

// Operator-defined option lists (per 2026-05-30 conversation).
// 'Paid by' is a person OR company — Personal expands to specific people so the
// audit trail captures who actually paid. Stored as text on the order.
const PAID_BY_OPTIONS = [
  { value: "Company",          label: "Company" },
  { value: "Personal_Sachin",  label: "Personal — Sachin" },
  { value: "Personal_Deepoo",  label: "Personal — Deepoo" },
  { value: "Personal_Shivam",  label: "Personal — Shivam" },
] as const;

// Cash and Cheque carry no extra account detail. Card splits into Debit/Credit.
const PAYMENT_METHOD_OPTIONS = [
  "Cash",
  "UPI",
  "Bank_Transfer",
  "Debit_Card",
  "Credit_Card",
  "Wallet",
  "Cheque",
] as const;

const WALLET_PROVIDERS = ["Cred Flash", "Amazon Flash"] as const;

// Sub-field label per method. Drives the account-detail input's label/placeholder.
function accountDetailLabel(method: string): { label: string; placeholder: string } | null {
  switch (method) {
    case "UPI":           return { label: "UPI ID",            placeholder: "e.g. name@okhdfcbank" };
    case "Bank_Transfer": return { label: "Bank detail",       placeholder: "e.g. HDFC ****1234 / IFSC HDFC0000001" };
    case "Debit_Card":    return { label: "Bank · last 4",     placeholder: "e.g. HDFC ****1234" };
    case "Credit_Card":   return { label: "Bank · last 4",     placeholder: "e.g. SBI ****5678" };
    case "Wallet":        return { label: "Wallet provider",   placeholder: "Select wallet" };
    default:              return null; // Cash, Cheque
  }
}

interface StockInOrderDetailSheetProps {
  orderId: number;
  onClose: () => void;
}

export function StockInOrderDetailSheet({ orderId, onClose }: StockInOrderDetailSheetProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showAddRefund, setShowAddRefund] = useState(false);
  const [refundTrigger, setRefundTrigger] = useState<string>(StockInOrderRefundTrigger.Price_Drop);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundNotes, setRefundNotes] = useState("");

  // Payment form state (Draft → Record Payment inline, and Edit Payment Details).
  const [prepaidAmount, setPrepaidAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paidBy, setPaidBy] = useState("Company");
  const [paymentAccountDetail, setPaymentAccountDetail] = useState("");
  const [paymentProofUrl, setPaymentProofUrl] = useState<string | null>(null);

  const { data: order, isLoading } = useGetStockInOrder(orderId, {
    query: {
      enabled: orderId > 0,
      queryKey: getGetStockInOrderQueryKey(orderId),
    },
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: getGetStockInOrderQueryKey(orderId) });
    qc.invalidateQueries({ queryKey: getListStockInOrdersQueryKey() });
    // Status flips into Delivered create a stock-in receipt; refresh the list
    // so it appears under External Receipts immediately.
    qc.invalidateQueries({ queryKey: getListStockInQueryKey() });
  }

  const paymentMut = useRecordStockInOrderPayment({
    mutation: {
      onSuccess: (updated) => {
        invalidate();
        if ("warning" in updated && updated.warning) {
          toast({ title: String(updated.warning), variant: "destructive" });
        } else {
          toast({ title: "Payment recorded. Order is now Placed." });
        }
      },
      onError: () => toast({ title: "Failed to record payment.", variant: "destructive" }),
    },
  });

  const cancelMut = useCancelStockInOrder({
    mutation: {
      onSuccess: () => { invalidate(); toast({ title: "Order cancelled." }); },
      onError: () => toast({ title: "Failed to cancel order.", variant: "destructive" }),
    },
  });

  const closeMut = useCloseStockInOrder({
    mutation: {
      onSuccess: () => { invalidate(); toast({ title: "Order closed." }); },
      onError: () => toast({ title: "Failed to close order.", variant: "destructive" }),
    },
  });

  const createRefundMut = useCreateStockInOrderRefundLine({
    mutation: {
      onSuccess: () => {
        invalidate();
        setShowAddRefund(false);
        setRefundAmount("");
        setRefundNotes("");
        toast({ title: "Refund line added." });
      },
      onError: () => toast({ title: "Failed to add refund line.", variant: "destructive" }),
    },
  });

  const updateRefundMut = useUpdateStockInOrderRefundLine({
    mutation: {
      onSuccess: () => { invalidate(); toast({ title: "Refund marked as received." }); },
      onError: () => toast({ title: "Failed to update refund.", variant: "destructive" }),
    },
  });

  // Quick "Update Payment Proof" action — separate from the broader Edit
  // Payment Details form so the operator can drop in just the screenshot.
  const [editProofOpen, setEditProofOpen] = useState(false);
  const [proofDraft, setProofDraft] = useState<string | null>(null);
  function handleOpenProofDialog() {
    setProofDraft(order?.paymentProofUrl ?? null);
    setEditProofOpen(true);
  }
  function handleSaveProof() {
    updatePaymentDetailsMut.mutate(
      { id: orderId, data: { paymentProofUrl: proofDraft } },
      { onSuccess: () => setEditProofOpen(false) },
    );
  }

  // Edit non-payment header fields (reference id / order proof / notes).
  const [editHeaderOpen, setEditHeaderOpen] = useState(false);
  const [hdrReferenceId, setHdrReferenceId] = useState("");
  const [hdrNotes, setHdrNotes] = useState("");
  const [hdrOrderProofUrl, setHdrOrderProofUrl] = useState<string | null>(null);
  const updateHeaderMut = useUpdateStockInOrderHeader({
    mutation: {
      onSuccess: () => { invalidate(); setEditHeaderOpen(false); toast({ title: "Order header updated." }); },
      onError: () => toast({ title: "Failed to update order header.", variant: "destructive" }),
    },
  });
  function handleEditHeaderClick() {
    setHdrReferenceId(order?.referenceId ?? "");
    setHdrNotes(order?.notes ?? "");
    setHdrOrderProofUrl(order?.orderProofUrl ?? null);
    setEditHeaderOpen(true);
  }
  function handleSaveHeader() {
    updateHeaderMut.mutate({
      id: orderId,
      data: {
        referenceId:   hdrReferenceId || null,
        notes:         hdrNotes || null,
        orderProofUrl: hdrOrderProofUrl,
      },
    });
  }

  // Edit payment transaction details on an already-placed order (operator
  // request 2026-05-30). DOES NOT change status or prepaid amount.
  const [editPaymentMode, setEditPaymentMode] = useState(false);
  const updatePaymentDetailsMut = useUpdateStockInOrderPaymentDetails({
    mutation: {
      onSuccess: (updated) => {
        // Push the freshly-returned order straight into the detail
        // cache so the read-only view re-renders with the new amount
        // immediately (no flash of stale ₹ while invalidateQueries
        // races a refetch). invalidate() still fires so the list /
        // receipts views catch up.
        if (updated && typeof updated === "object" && "id" in updated) {
          qc.setQueryData(getGetStockInOrderQueryKey(orderId), (prev: unknown) => {
            // Server's PATCH response is the bare order row — the
            // detail GET adds vendorName / cartLines / deliveries /
            // refundLines / reconciliation. Merge so we keep those.
            if (prev && typeof prev === "object") {
              return { ...prev, ...(updated as unknown as Record<string, unknown>) };
            }
            return updated;
          });
        }
        invalidate();
        setEditPaymentMode(false);
        toast({ title: "Payment details updated." });
      },
      onError: (err: unknown) => {
        // Surface server-side validation issues so the operator knows
        // which field tripped — "Failed" alone gave no signal.
        const detail =
          err && typeof err === "object" && "message" in err && typeof (err as { message?: unknown }).message === "string"
            ? (err as { message: string }).message
            : "Failed to update payment details.";
        toast({ title: detail, variant: "destructive" });
      },
    },
  });

  function handleEditPaymentClick() {
    // Prefill state from order so the existing form inputs render the current values.
    setPrepaidAmount(order?.prepaidAmount != null ? String(order.prepaidAmount) : "");
    setPaidBy(order?.paidBy ?? "Company");
    setPaymentMethod(order?.paymentMethod ?? "");
    setPaymentAccountDetail(order?.paymentAccountDetail ?? "");
    setPaymentReference(order?.paymentReference ?? "");
    setPaymentDate(order?.paymentDate ? order.paymentDate.slice(0, 10) : "");
    setPaymentProofUrl(order?.paymentProofUrl ?? null);
    setEditPaymentMode(true);
  }

  function handleSavePaymentDetails() {
    // The Edit Payment Details panel keeps the prepaid amount field
    // visible at all times, so the operator's intent is always the
    // value sitting in the input — even when they didn't change it.
    // Sending it unconditionally (instead of conditionally spreading)
    // closes a class of "save succeeds but value reverts" bugs where
    // a quirky state path left prepaidAmount blank in transit while
    // the rest of the body went through.
    const prepaidNum = prepaidAmount === "" ? null : Number(prepaidAmount);
    if (prepaidNum != null && (!Number.isFinite(prepaidNum) || prepaidNum < 0)) {
      toast({ title: "Enter a valid prepaid amount.", variant: "destructive" });
      return;
    }
    if (prepaidNum == null) {
      toast({ title: "Prepaid amount is required.", variant: "destructive" });
      return;
    }
    updatePaymentDetailsMut.mutate({
      id: orderId,
      data: {
        prepaidAmount: prepaidNum,
        paidBy: paidBy || null,
        paymentMethod: paymentMethod || null,
        paymentAccountDetail: paymentAccountDetail || null,
        paymentReference: paymentReference || null,
        paymentDate: paymentDate || null,
        paymentProofUrl: paymentProofUrl,
      },
    });
  }

  // Cart-line management — add new, edit qty/price/notes, delete (no-delivery only).
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [editLineId, setEditLineId] = useState<number | null>(null);
  const [deleteLineId, setDeleteLineId] = useState<number | null>(null);
  type CartLineDraft = { itemId: number | null; quantityOrdered: string; expectedUnitPrice: string; notes: string };
  const [lineDraft, setLineDraft] = useState<CartLineDraft>({ itemId: null, quantityOrdered: "1", expectedUnitPrice: "", notes: "" });

  const addLineMut = useAddStockInOrderCartLine({
    mutation: {
      onSuccess: () => { invalidate(); setAddLineOpen(false); setLineDraft({ itemId: null, quantityOrdered: "1", expectedUnitPrice: "", notes: "" }); toast({ title: "Cart line added." }); },
      onError: () => toast({ title: "Failed to add cart line.", variant: "destructive" }),
    },
  });
  const updateLineMut = useUpdateStockInOrderCartLine({
    mutation: {
      onSuccess: () => { invalidate(); setEditLineId(null); toast({ title: "Cart line updated." }); },
      onError: () => toast({ title: "Failed to update cart line.", variant: "destructive" }),
    },
  });
  const deleteLineMut = useDeleteStockInOrderCartLine({
    mutation: {
      onSuccess: () => { invalidate(); setDeleteLineId(null); toast({ title: "Cart line removed." }); },
      onError: (err: unknown) => {
        const msg = err && typeof err === "object" && "response" in err
          ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? "Failed to delete cart line.")
          : "Failed to delete cart line.";
        toast({ title: msg, variant: "destructive" });
      },
    },
  });

  function openAddLine() {
    setLineDraft({ itemId: null, quantityOrdered: "1", expectedUnitPrice: "", notes: "" });
    setAddLineOpen(true);
  }
  function openEditLine(line: StockInOrderCartLine) {
    setLineDraft({
      itemId: line.itemId,
      quantityOrdered: String(line.quantityOrdered ?? ""),
      expectedUnitPrice: String(line.expectedUnitPrice ?? ""),
      notes: line.notes ?? "",
    });
    setEditLineId(line.id);
  }
  function handleSaveAddLine() {
    if (lineDraft.itemId == null) { toast({ title: "Pick an item.", variant: "destructive" }); return; }
    addLineMut.mutate({
      id: orderId,
      data: {
        itemId: lineDraft.itemId,
        quantityOrdered: Number(lineDraft.quantityOrdered) || 1,
        expectedUnitPrice: Number(lineDraft.expectedUnitPrice) || 0,
        notes: lineDraft.notes || undefined,
      },
    });
  }
  function handleSaveEditLine() {
    if (editLineId == null) return;
    updateLineMut.mutate({
      id: orderId,
      lineId: editLineId,
      data: {
        quantityOrdered: Number(lineDraft.quantityOrdered) || undefined,
        expectedUnitPrice: Number(lineDraft.expectedUnitPrice) || undefined,
        notes: lineDraft.notes,
      },
    });
  }

  // R3 — flip a cart line's lifecycle status (Returned / Refunded / Personal / …).
  // Server may auto-create a stock-in receipt when transitioning to Delivered
  // and surface it as `autoReceipt: { stockInId }` on the response.
  const updateCartLineStatusMut = useUpdateStockInOrderCartLineStatus({
    mutation: {
      onSuccess: (resp) => {
        invalidate();
        // Surface the auto-receipt creation so the operator knows a stock-in
        // entry just landed under External Receipts.
        const auto = (resp as { autoReceipt?: { stockInId: number } } | undefined)?.autoReceipt;
        if (auto?.stockInId) {
          toast({ title: `Cart line marked Delivered · receipt STK-IN-${String(auto.stockInId).padStart(3, "0")} created.` });
        } else {
          toast({ title: "Cart line status updated." });
        }
      },
      onError: (err: unknown) => {
        const msg = err && typeof err === "object" && "response" in err
          ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? "Failed to update cart line status.")
          : "Failed to update cart line status.";
        toast({ title: msg, variant: "destructive" });
      },
    },
  });

  function handleRecordPayment() {
    if (!prepaidAmount || Number(prepaidAmount) <= 0) {
      toast({ title: "Enter a valid prepaid amount.", variant: "destructive" });
      return;
    }
    paymentMut.mutate({
      id: orderId,
      data: {
        prepaidAmount: Number(prepaidAmount),
        paidBy: paidBy || undefined,
        paymentMethod: paymentMethod || undefined,
        paymentAccountDetail: paymentAccountDetail || undefined,
        paymentReference: paymentReference || undefined,
        paymentDate: paymentDate || undefined,
        paymentProofUrl: paymentProofUrl ?? undefined,
      },
    });
  }

  function handleAddRefund() {
    if (!refundAmount || Number(refundAmount) <= 0) {
      toast({ title: "Enter a valid refund amount.", variant: "destructive" });
      return;
    }
    createRefundMut.mutate({
      id: orderId,
      data: {
        trigger: refundTrigger as (typeof StockInOrderRefundTrigger)[keyof typeof StockInOrderRefundTrigger],
        amount: Number(refundAmount),
        notes: refundNotes || undefined,
      },
    });
  }

  function handleMarkRefundReceived(refundLine: StockInOrderRefundLine) {
    updateRefundMut.mutate({
      id: orderId,
      lineId: refundLine.id,
      data: {
        status: UpdateOrderRefundLineBodyStatus.Received,
      },
    });
  }

  return (
    <>
      <Sheet open={orderId > 0} onOpenChange={(o) => !o && onClose()}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
          {isLoading || !order ? (
            <div className="space-y-3 pt-6">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (
            <OrderDetailContent
              order={order}
              showDeliveryForm={showDeliveryForm}
              setShowDeliveryForm={setShowDeliveryForm}
              showCancelDialog={showCancelDialog}
              setShowCancelDialog={setShowCancelDialog}
              showCloseDialog={showCloseDialog}
              setShowCloseDialog={setShowCloseDialog}
              showAddRefund={showAddRefund}
              setShowAddRefund={setShowAddRefund}
              refundTrigger={refundTrigger}
              setRefundTrigger={setRefundTrigger}
              refundAmount={refundAmount}
              setRefundAmount={setRefundAmount}
              refundNotes={refundNotes}
              setRefundNotes={setRefundNotes}
              prepaidAmount={prepaidAmount}
              setPrepaidAmount={setPrepaidAmount}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              paymentReference={paymentReference}
              setPaymentReference={setPaymentReference}
              paymentDate={paymentDate}
              setPaymentDate={setPaymentDate}
              paidBy={paidBy}
              setPaidBy={setPaidBy}
              paymentAccountDetail={paymentAccountDetail}
              setPaymentAccountDetail={setPaymentAccountDetail}
              paymentProofUrl={paymentProofUrl}
              setPaymentProofUrl={setPaymentProofUrl}
              onRecordPayment={handleRecordPayment}
              onMarkRefundReceived={handleMarkRefundReceived}
              onAddRefund={handleAddRefund}
              onCancelConfirm={() => {
                cancelMut.mutate({ id: orderId, data: {} });
                setShowCancelDialog(false);
              }}
              onCloseConfirm={() => {
                closeMut.mutate({ id: orderId, data: { confirmShortfall: true } });
                setShowCloseDialog(false);
              }}
              paymentMutPending={paymentMut.isPending}
              cancelMutPending={cancelMut.isPending}
              closeMutPending={closeMut.isPending}
              createRefundPending={createRefundMut.isPending}
              updateRefundPending={updateRefundMut.isPending}
              onCartLineStatusChange={(lineId, status) =>
                updateCartLineStatusMut.mutate({ id: orderId, lineId, data: { status } })
              }
              cartLineStatusPending={updateCartLineStatusMut.isPending}
              editPaymentMode={editPaymentMode}
              onEditPaymentClick={handleEditPaymentClick}
              onCancelEditPayment={() => setEditPaymentMode(false)}
              onSavePaymentDetails={handleSavePaymentDetails}
              editPaymentPending={updatePaymentDetailsMut.isPending}
              onEditHeaderClick={handleEditHeaderClick}
              onUpdateProofClick={handleOpenProofDialog}
              onAddCartLine={openAddLine}
              onEditCartLine={openEditLine}
              onDeleteCartLine={(lineId) => setDeleteLineId(lineId)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Add cart line */}
      <Dialog open={addLineOpen} onOpenChange={setAddLineOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add cart line</DialogTitle>
            <DialogDescription>Append a new item to this order.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Item</Label>
              <ItemCombobox
                value={lineDraft.itemId}
                onChange={(id) => setLineDraft((d) => ({ ...d, itemId: id }))}
                showTier={false}
                placeholder="Search item…"
                // RECIPE-DISPLAY-02: "Add cart line" dialog on an existing Stock-In order —
                // recipe items are cooked to order, never appear on a receipt.
                excludeRecipe
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Quantity</Label>
                <Input type="number" min={1} step={1} value={lineDraft.quantityOrdered} onChange={(e) => setLineDraft((d) => ({ ...d, quantityOrdered: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Expected unit price (₹)</Label>
                <Input type="number" min={0} step="0.01" value={lineDraft.expectedUnitPrice} onChange={(e) => setLineDraft((d) => ({ ...d, expectedUnitPrice: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={lineDraft.notes} onChange={(e) => setLineDraft((d) => ({ ...d, notes: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLineOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveAddLine} disabled={addLineMut.isPending || lineDraft.itemId == null}>
              {addLineMut.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Add line
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit cart line */}
      <Dialog open={editLineId !== null} onOpenChange={(o) => { if (!o) setEditLineId(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit cart line</DialogTitle>
            <DialogDescription>Item can't change — to swap items, delete this line and add a new one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Quantity</Label>
                <Input type="number" min={0} step="0.01" value={lineDraft.quantityOrdered} onChange={(e) => setLineDraft((d) => ({ ...d, quantityOrdered: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Expected unit price (₹)</Label>
                <Input type="number" min={0} step="0.01" value={lineDraft.expectedUnitPrice} onChange={(e) => setLineDraft((d) => ({ ...d, expectedUnitPrice: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={lineDraft.notes} onChange={(e) => setLineDraft((d) => ({ ...d, notes: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLineId(null)}>Cancel</Button>
            <Button onClick={handleSaveEditLine} disabled={updateLineMut.isPending}>
              {updateLineMut.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete cart line confirm */}
      <AlertDialog open={deleteLineId !== null} onOpenChange={(o) => { if (!o) setDeleteLineId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this cart line?</AlertDialogTitle>
            <AlertDialogDescription>
              Cart lines with existing deliveries can't be removed — the server will refuse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteLineId != null && deleteLineMut.mutate({ id: orderId, lineId: deleteLineId })}
              disabled={deleteLineMut.isPending}
            >
              {deleteLineMut.isPending ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick Update Payment Proof — upload only, no other fields. */}
      <Dialog open={editProofOpen} onOpenChange={setEditProofOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update payment proof</DialogTitle>
            <DialogDescription>Upload (or replace) the payment screenshot / receipt for this order.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <PaymentProofField value={proofDraft} onChange={setProofDraft} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditProofOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveProof} disabled={updatePaymentDetailsMut.isPending}>
              {updatePaymentDetailsMut.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit order header (reference id / order proof / notes) */}
      <Dialog open={editHeaderOpen} onOpenChange={setEditHeaderOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit order header</DialogTitle>
            <DialogDescription>Reference id, order proof, and notes. Status and payment unchanged.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Reference ID</Label>
              <Input value={hdrReferenceId} onChange={(e) => setHdrReferenceId(e.target.value)} placeholder="Vendor PO #, e-comm order id, …" />
            </div>
            <div className="space-y-1">
              <Label>Order proof</Label>
              <OrderProofUploader value={hdrOrderProofUrl} onChange={setHdrOrderProofUrl} />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={hdrNotes} onChange={(e) => setHdrNotes(e.target.value)} placeholder="Internal notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditHeaderOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveHeader} disabled={updateHeaderMut.isPending}>
              {updateHeaderMut.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this order?</AlertDialogTitle>
            <AlertDialogDescription>
              The order will be cancelled. The system will auto-create a Cancellation refund line
              for the undelivered prepaid value.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep order</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                cancelMut.mutate({ id: orderId, data: {} });
                setShowCancelDialog(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancel order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Close with shortfall confirmation dialog */}
      <AlertDialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close order with undelivered items?</AlertDialogTitle>
            <AlertDialogDescription>
              Some items were not fully delivered. Closing will auto-create an Under_Delivery refund
              line for the shortfall value. Confirm to proceed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go back</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                closeMut.mutate({ id: orderId, data: { confirmShortfall: true } });
                setShowCloseDialog(false);
              }}
            >
              Close order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Inner content component ─────────────────────────────────────────────────

interface ContentProps {
  order: StockInOrder;
  showDeliveryForm: boolean;
  setShowDeliveryForm: (v: boolean) => void;
  showCancelDialog: boolean;
  setShowCancelDialog: (v: boolean) => void;
  showCloseDialog: boolean;
  setShowCloseDialog: (v: boolean) => void;
  showAddRefund: boolean;
  setShowAddRefund: (v: boolean) => void;
  refundTrigger: string;
  setRefundTrigger: (v: string) => void;
  refundAmount: string;
  setRefundAmount: (v: string) => void;
  refundNotes: string;
  setRefundNotes: (v: string) => void;
  prepaidAmount: string;
  setPrepaidAmount: (v: string) => void;
  paymentMethod: string;
  setPaymentMethod: (v: string) => void;
  paymentReference: string;
  setPaymentReference: (v: string) => void;
  paymentDate: string;
  setPaymentDate: (v: string) => void;
  paidBy: string;
  setPaidBy: (v: string) => void;
  paymentAccountDetail: string;
  setPaymentAccountDetail: (v: string) => void;
  paymentProofUrl: string | null;
  setPaymentProofUrl: (v: string | null) => void;
  onRecordPayment: () => void;
  onMarkRefundReceived: (line: StockInOrderRefundLine) => void;
  onAddRefund: () => void;
  onCancelConfirm: () => void;
  onCloseConfirm: () => void;
  paymentMutPending: boolean;
  cancelMutPending: boolean;
  closeMutPending: boolean;
  createRefundPending: boolean;
  updateRefundPending: boolean;
  onCartLineStatusChange: (lineId: number, status: StockInOrderCartLineStatus) => void;
  cartLineStatusPending: boolean;
  editPaymentMode: boolean;
  onEditPaymentClick: () => void;
  onCancelEditPayment: () => void;
  onSavePaymentDetails: () => void;
  editPaymentPending: boolean;
  onEditHeaderClick: () => void;
  onUpdateProofClick: () => void;
  onAddCartLine: () => void;
  onEditCartLine: (line: StockInOrderCartLine) => void;
  onDeleteCartLine: (lineId: number) => void;
}

function OrderDetailContent({
  order,
  showDeliveryForm,
  setShowDeliveryForm,
  setShowCancelDialog,
  setShowCloseDialog,
  showAddRefund,
  setShowAddRefund,
  refundTrigger,
  setRefundTrigger,
  refundAmount,
  setRefundAmount,
  refundNotes,
  setRefundNotes,
  prepaidAmount,
  setPrepaidAmount,
  paymentMethod,
  setPaymentMethod,
  paymentReference,
  setPaymentReference,
  paymentDate,
  setPaymentDate,
  paidBy,
  setPaidBy,
  paymentAccountDetail,
  setPaymentAccountDetail,
  paymentProofUrl,
  setPaymentProofUrl,
  onRecordPayment,
  onMarkRefundReceived,
  onAddRefund,
  paymentMutPending,
  cancelMutPending,
  closeMutPending,
  createRefundPending,
  updateRefundPending,
  onCartLineStatusChange,
  cartLineStatusPending,
  editPaymentMode,
  onEditPaymentClick,
  onCancelEditPayment,
  onSavePaymentDetails,
  editPaymentPending,
  onEditHeaderClick,
  onUpdateProofClick,
  onAddCartLine,
  onEditCartLine,
  onDeleteCartLine,
}: ContentProps) {
  const cartLines = order.cartLines ?? [];
  const deliveries = order.deliveries ?? [];
  const refundLines = order.refundLines ?? [];
  const recon = order.reconciliation;

  const isReadOnly =
    order.status === StockInOrderStatus.Closed ||
    order.status === StockInOrderStatus.Cancelled;

  // Compute delivered qty per cart line itemId
  const deliveredQtyByItemId = new Map<number, number>();
  for (const d of deliveries) {
    for (const li of d.lineItems ?? []) {
      if (li.itemId != null) {
        deliveredQtyByItemId.set(li.itemId, (deliveredQtyByItemId.get(li.itemId) ?? 0) + Number(li.quantityReceived ?? 0));
      }
    }
  }

  return (
    <div className="space-y-6">
      <SheetHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            {(() => {
              const ref = order.orderId ?? ordId(order.id);
              const isLegacy = ref.startsWith("LEGACY-");
              const displayId = isLegacy ? ref.replace(/^LEGACY-/, "ORD-") : ref;
              return (
                <SheetTitle className="font-mono flex items-center gap-2">
                  <span>{displayId}</span>
                  {isLegacy && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                      Legacy
                    </span>
                  )}
                </SheetTitle>
              );
            })()}
            <div className="mt-1.5 flex items-center gap-2">
              <StatusBadge status={order.status} />
              <span className="text-xs text-muted-foreground">
                Order date {new Date(order.paymentDate ?? order.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </SheetHeader>

      {/* ── Section 1: Order header ─────────────────────────────────────── */}
      <section className="space-y-2 rounded-md border p-4">
        <p className="text-sm font-semibold">Order Details</p>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-muted-foreground">Vendor</dt>
          <dd>{order.vendorName ?? order.vendorNameFreeText ?? (order.vendorId != null ? `Vendor #${order.vendorId}` : "—")}</dd>

          <dt className="text-muted-foreground">Expected delivery</dt>
          <dd>
            {order.expectedDeliveryFrom
              ? `${new Date(order.expectedDeliveryFrom).toLocaleDateString()} – ${order.expectedDeliveryTo ? new Date(order.expectedDeliveryTo).toLocaleDateString() : "open"}`
              : "—"}
          </dd>

          <dt className="text-muted-foreground">Reference ID</dt>
          <dd className="font-mono text-xs">{order.referenceId ?? "—"}</dd>

          <dt className="text-muted-foreground">Order proof</dt>
          <dd>
            {order.orderProofUrl ? (
              <a
                href={order.orderProofUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-100"
              >
                <Eye className="h-3 w-3" />
                View
              </a>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </dd>

          {order.notes && (
            <>
              <dt className="text-muted-foreground">Notes</dt>
              <dd>{order.notes}</dd>
            </>
          )}
        </dl>
        {!isReadOnly && (
          <Button size="sm" variant="outline" onClick={onEditHeaderClick} className="h-7">
            Edit reference / proof / notes
          </Button>
        )}
      </section>

      {/* ── Section 2: Payment ──────────────────────────────────────────── */}
      <section className="space-y-3 rounded-md border p-4">
        <p className="text-sm font-semibold">Payment Transaction Details</p>
        {order.status === StockInOrderStatus.Draft ? (
          // Draft -> capture prepaid amount + full payment details, then POST /payment
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Record the upfront prepayment to place this order.
            </p>
            <div className="space-y-1">
              <label className="text-xs font-medium">Prepaid amount (₹)</label>
              <Input
                type="number"
                min={0}
                step="0.01"
                placeholder="0.00"
                value={prepaidAmount}
                onChange={(e) => setPrepaidAmount(e.target.value)}
                className="max-w-[220px]"
              />
            </div>
            <PaymentFieldsEditor
              paidBy={paidBy} setPaidBy={setPaidBy}
              paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
              paymentAccountDetail={paymentAccountDetail} setPaymentAccountDetail={setPaymentAccountDetail}
              paymentReference={paymentReference} setPaymentReference={setPaymentReference}
              paymentDate={paymentDate} setPaymentDate={setPaymentDate}
              paymentProofUrl={paymentProofUrl} setPaymentProofUrl={setPaymentProofUrl}
            />
            <Button size="sm" onClick={onRecordPayment} disabled={paymentMutPending}>
              {paymentMutPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Record Payment &amp; Place Order
            </Button>
          </div>
        ) : editPaymentMode ? (
          // Placed+ order, in-edit. Prepaid amount is now editable too (typo fix
          // case — operator corrects the stamped amount). Status is NOT touched.
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium">Prepaid amount (₹)</label>
              <Input
                type="number"
                min={0}
                step="0.01"
                placeholder="0.00"
                value={prepaidAmount}
                onChange={(e) => setPrepaidAmount(e.target.value)}
                className="max-w-[220px]"
              />
              <p className="text-[10px] text-muted-foreground">
                Leave unchanged to keep the existing amount. Audit-logged when changed.
              </p>
            </div>
            <PaymentFieldsEditor
              paidBy={paidBy} setPaidBy={setPaidBy}
              paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
              paymentAccountDetail={paymentAccountDetail} setPaymentAccountDetail={setPaymentAccountDetail}
              paymentReference={paymentReference} setPaymentReference={setPaymentReference}
              paymentDate={paymentDate} setPaymentDate={setPaymentDate}
              paymentProofUrl={paymentProofUrl} setPaymentProofUrl={setPaymentProofUrl}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={onSavePaymentDetails} disabled={editPaymentPending}>
                {editPaymentPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={onCancelEditPayment} disabled={editPaymentPending}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          // Placed+ order, read-only display.
          <div className="space-y-3">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-muted-foreground">Prepaid amount</dt>
              <dd className="font-mono">{fmtINR(order.prepaidAmount)}</dd>

              <dt className="text-muted-foreground">Paid by</dt>
              <dd>{(order.paidBy ?? "—").replace(/_/g, " — ")}</dd>

              <dt className="text-muted-foreground">Method</dt>
              <dd>{order.paymentMethod?.replace(/_/g, " ") ?? "—"}</dd>

              {order.paymentAccountDetail && (
                <>
                  <dt className="text-muted-foreground">Account detail</dt>
                  <dd className="font-mono text-xs">{order.paymentAccountDetail}</dd>
                </>
              )}

              <dt className="text-muted-foreground">Reference</dt>
              <dd className="font-mono text-xs">{order.paymentReference ?? "—"}</dd>

              {order.paymentDate && (
                <>
                  <dt className="text-muted-foreground">Payment date</dt>
                  <dd>{new Date(order.paymentDate).toLocaleDateString()}</dd>
                </>
              )}

              <dt className="text-muted-foreground">Payment proof</dt>
              <dd>
                {order.paymentProofUrl ? (
                  <a
                    href={order.paymentProofUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-100"
                  >
                    <Eye className="h-3 w-3" />
                    View
                  </a>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </dd>
            </dl>
            {!isReadOnly && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={onEditPaymentClick} className="h-7">
                  Edit payment details
                </Button>
                <Button size="sm" variant="outline" onClick={onUpdateProofClick} className="h-7">
                  <Upload className="mr-1 h-3 w-3" />
                  Update payment proof
                </Button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Section 3: Cart lines ───────────────────────────────────────── */}
      <section className="space-y-2 rounded-md border p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">Cart Lines</p>
          {!isReadOnly && (
            <Button size="sm" variant="outline" onClick={onAddCartLine} className="h-7">
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add line
            </Button>
          )}
        </div>
        {cartLines.length === 0 ? (
          <p className="text-sm text-muted-foreground">No cart lines.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Ordered</TableHead>
                <TableHead className="text-right">Delivered</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead className="text-right">Expected price</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                {!isReadOnly && <TableHead className="w-[80px]" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {cartLines.map((cl: StockInOrderCartLine) => {
                // Use the server-computed quantityDelivered (joined SUM via
                // stock_in_line_items) — the old client-side derivation
                // walked d.lineItems which isn't on the detail response, so
                // it always read as 0.
                const delivered = Number(
                  (cl as { quantityDelivered?: number | string | null }).quantityDelivered ?? 0,
                );
                const remaining = cl.quantityOrdered - delivered;
                return (
                  <TableRow key={cl.id}>
                    <TableCell className="text-sm">
                      <div className="flex flex-col gap-0.5 leading-snug">
                        <span className="font-medium">{cl.itemName ?? `Item #${cl.itemId}`}</span>
                        {(cl.itemBrand || cl.itemSpecs) && (
                          <span className="text-xs text-muted-foreground">
                            {[cl.itemBrand, cl.itemSpecs].filter(Boolean).join(" · ")}
                          </span>
                        )}
                        {cl.itemCurrentStock != null && (
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {cl.itemCurrentStock} {cl.itemUom ?? "units"} in stock
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{cl.quantityOrdered}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{delivered}</TableCell>
                    <TableCell
                      className={cn(
                        "text-right text-sm tabular-nums",
                        remaining > 0 ? "text-amber-600 font-medium" : "text-muted-foreground",
                      )}
                    >
                      {remaining}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">
                      {fmtINR(Number(cl.expectedUnitPrice))}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">
                      {fmtINR(Number(cl.expectedUnitPrice) * Number(cl.quantityOrdered))}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={cl.status}
                        onValueChange={(v) => {
                          if (v === cl.status) return;
                          onCartLineStatusChange(cl.id, v as StockInOrderCartLineStatus);
                        }}
                        disabled={cartLineStatusPending || isReadOnly}
                      >
                        <SelectTrigger className={cn("h-7 text-xs", cartLineStatusPill(cl.status))}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CART_LINE_STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s.replace(/_/g, " ")}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    {!isReadOnly && (
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onEditCartLine(cl)}
                            title="Edit cart line"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={() => onDeleteCartLine(cl.id)}
                            title={delivered > 0 ? "Has deliveries — cannot delete" : "Remove cart line"}
                            disabled={delivered > 0}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell className="text-sm font-semibold">Grand total</TableCell>
                <TableCell className="text-right text-sm font-semibold tabular-nums">
                  {cartLines.reduce((sum, cl) => sum + Number(cl.quantityOrdered), 0)}
                </TableCell>
                <TableCell colSpan={3} />
                <TableCell className="text-right font-mono text-sm font-semibold tabular-nums">
                  {fmtINR(
                    cartLines.reduce(
                      (sum, cl) => sum + Number(cl.expectedUnitPrice) * Number(cl.quantityOrdered),
                      0,
                    ),
                  )}
                </TableCell>
                <TableCell colSpan={isReadOnly ? 1 : 2} />
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </section>

      {/* ── Section 4: Linked deliveries ────────────────────────────────── */}
      <section className="space-y-2 rounded-md border p-4">
        <p className="text-sm font-semibold">Linked Deliveries</p>
        {deliveries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No deliveries recorded yet.</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>STK-IN</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Linked</TableHead>
                  <TableHead className="text-right">Pending link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.map((d) => {
                  const linked = Number((d as { linkedAmount?: number | string | null }).linkedAmount ?? 0);
                  const unlinked = Number((d as { unlinkedAmount?: number | string | null }).unlinkedAmount ?? 0);
                  return (
                    <TableRow key={d.id}>
                      <TableCell className="font-mono text-xs">{stkInId(d.id)}</TableCell>
                      <TableCell className="text-sm">{new Date(d.transactionDate).toLocaleDateString()}</TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-2">
                          <span>{d.invoiceNumber ?? "—"}</span>
                          {d.invoicePdfUrl ? (
                            <a
                              href={d.invoicePdfUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700 hover:bg-blue-100"
                              title="Open invoice PDF"
                            >
                              PDF
                            </a>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm tabular-nums">
                        {fmtINR(Number(d.grandTotal ?? 0))}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm tabular-nums text-green-700">
                        {fmtINR(linked)}
                      </TableCell>
                      <TableCell className={cn("text-right font-mono text-sm tabular-nums", unlinked > 0 ? "text-amber-700 font-medium" : "text-muted-foreground")}>
                        {fmtINR(unlinked)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {/* Per-receipt unlinked items panel — always rendered so the
                empty state is visible (and the operator knows the absence
                is meaningful, not a missing UI element). Receipts whose
                line items all match cart lines show 'all matched'; ones
                with substitutions / off-cart items list them in amber. */}
            <div className="space-y-2 pt-2 border-t">
              <p className="text-xs font-semibold">Unlinked items (substitutions / off-cart on receipt)</p>
              {(() => {
                const withUnlinked = deliveries.filter(
                  (d) => ((d as { unlinkedItems?: Array<unknown> | null }).unlinkedItems?.length ?? 0) > 0,
                );
                if (withUnlinked.length === 0) {
                  return (
                    <p className="text-xs text-muted-foreground">
                      All receipt line items match a cart line — no substitutions.
                    </p>
                  );
                }
                return withUnlinked.map((d) => {
                  const items = (d as { unlinkedItems?: Array<{ itemId: number; itemName: string | null; itemBrand: string | null; itemSpecs: string | null; quantityReceived: number; lineValue: number }> | null }).unlinkedItems ?? [];
                  return (
                    <div key={`unl-${d.id}`} className="rounded-md border bg-amber-50/50 px-3 py-2 text-xs">
                      <p className="font-mono mb-1">{stkInId(d.id)}</p>
                      <ul className="space-y-0.5">
                        {items.map((u, i) => (
                          <li key={i} className="flex justify-between gap-3">
                            <span>
                              {u.itemName ?? `Item #${u.itemId}`}
                              {u.itemBrand ? <span className="text-muted-foreground"> · {u.itemBrand}</span> : null}
                              {u.itemSpecs ? <span className="text-muted-foreground"> · {u.itemSpecs}</span> : null}
                              <span className="text-muted-foreground"> · qty {u.quantityReceived}</span>
                            </span>
                            <span className="font-mono tabular-nums">{fmtINR(u.lineValue)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                });
              })()}
            </div>
          </>
        )}
      </section>

      {/* ── Section 4b: Non Stock-In Delivered ──────────────────────────
          Cart lines that the operator has marked status='Delivered' but
          where the actual stock_in_line_items don't cover the full ordered
          qty. These are items received in practice but not yet recorded as
          a formal receipt. Helps the operator see the gap between the
          status flag (label) and actual receipts (truth). */}
      {(() => {
        const nonStockInRows = (order.cartLines ?? [])
          .map((cl) => {
            const delivered = Number((cl as { quantityDelivered?: number | string | null }).quantityDelivered ?? 0);
            const remainingQty = Math.max(0, cl.quantityOrdered - delivered);
            const lineValue = remainingQty * Number(cl.expectedUnitPrice);
            return { cl, remainingQty, lineValue };
          })
          .filter((r) => r.cl.status === "Delivered" && r.remainingQty > 0);
        const nonStockInTotal = nonStockInRows.reduce((s, r) => s + r.lineValue, 0);
        return (
          <section className="space-y-2 rounded-md border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">Non Stock-In Delivered</p>
              <span className={cn("font-mono text-sm tabular-nums", nonStockInTotal > 0 ? "text-amber-700 font-medium" : "text-muted-foreground")}>
                {fmtINR(nonStockInTotal)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Cart lines flagged <span className="font-medium">Delivered</span> but with no
              matching receipt (or partial receipt). These are received in
              practice but not yet captured as a formal stock-in.
            </p>
            {nonStockInRows.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Nothing — every Delivered line is covered by a receipt.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Qty without receipt</TableHead>
                    <TableHead className="text-right">Expected price</TableHead>
                    <TableHead className="text-right">Line value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {nonStockInRows.map(({ cl, remainingQty, lineValue }) => (
                    <TableRow key={`nsi-${cl.id}`}>
                      <TableCell className="text-sm">
                        {(cl as { itemName?: string | null }).itemName ?? `Item #${cl.itemId}`}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{remainingQty}</TableCell>
                      <TableCell className="text-right font-mono text-sm tabular-nums">
                        {fmtINR(Number(cl.expectedUnitPrice))}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm tabular-nums">
                        {fmtINR(lineValue)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>
        );
      })()}

      {/* ── Section 5: Refund lines ──────────────────────────────────────── */}
      <section className="space-y-2 rounded-md border p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">Refund Lines</p>
          {!isReadOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddRefund(!showAddRefund)}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add Refund
            </Button>
          )}
        </div>

        {showAddRefund && (
          <div className="space-y-3 rounded-md bg-muted/40 p-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Trigger</label>
                <select
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={refundTrigger}
                  onChange={(e) => setRefundTrigger(e.target.value)}
                >
                  {Object.values(StockInOrderRefundTrigger).map((t) => (
                    <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Amount (₹)</label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Notes</label>
              <Input
                placeholder="Optional notes…"
                value={refundNotes}
                onChange={(e) => setRefundNotes(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={onAddRefund} disabled={createRefundPending}>
                {createRefundPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                Add
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAddRefund(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {refundLines.length === 0 ? (
          <p className="text-sm text-muted-foreground">No refund lines.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trigger</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {refundLines.map((rl: StockInOrderRefundLine) => (
                <TableRow key={rl.id}>
                  <TableCell className="text-sm">{rl.trigger.replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-right font-mono text-sm tabular-nums">
                    {fmtINR(Number(rl.amount))}
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "inline-flex rounded px-1.5 py-0.5 text-xs font-medium",
                        rl.status === "Received"
                          ? "bg-green-100 text-green-700"
                          : "bg-amber-100 text-amber-700",
                      )}
                    >
                      {rl.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    {!isReadOnly && rl.status !== "Received" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={updateRefundPending}
                        onClick={() => onMarkRefundReceived(rl)}
                      >
                        Mark received
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      {/* ── Section 6: Reconciliation summary ──────────────────────────── */}
      {/* Compute cart-side gaps for surface in Reconciliation:
            orderTotal      = Σ qty_ordered × expected_unit_price
            undeliveredValue = orderTotal − deliveredValue (cart items not received yet) */}
      {(() => {
        const orderTotal = (order.cartLines ?? []).reduce(
          (s, cl) => s + cl.quantityOrdered * Number(cl.expectedUnitPrice),
          0,
        );
        const deliveredValue = Number(recon?.deliveredValue ?? 0);
        // Non Stock-In Delivered — status='Delivered' lines with qty missing from receipts.
        const nonStockInDelivered = (order.cartLines ?? []).reduce((s, cl) => {
          if (cl.status !== "Delivered") return s;
          const d = Number((cl as { quantityDelivered?: number | string | null }).quantityDelivered ?? 0);
          const remainingQty = Math.max(0, cl.quantityOrdered - d);
          return s + remainingQty * Number(cl.expectedUnitPrice);
        }, 0);
        const undelivered = Math.max(0, orderTotal - deliveredValue - nonStockInDelivered);
        return (
      <section className="space-y-2 rounded-md border p-4">
        <p className="text-sm font-semibold">Reconciliation</p>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-muted-foreground">Order total (cart)</dt>
          <dd className="font-mono">{fmtINR(orderTotal)}</dd>

          <dt className="text-muted-foreground">Delivered value (with receipt)</dt>
          <dd className="font-mono">{fmtINR(deliveredValue)}</dd>

          <dt className="text-muted-foreground">Non stock-in delivered</dt>
          <dd className={cn("font-mono", nonStockInDelivered > 0 ? "text-amber-600" : "text-muted-foreground")}>
            {fmtINR(nonStockInDelivered)}
          </dd>

          <dt className="text-muted-foreground">Pending (not yet delivered)</dt>
          <dd className={cn("font-mono", undelivered > 0 ? "text-amber-600" : "text-muted-foreground")}>
            {fmtINR(undelivered)}
          </dd>

          <Separator className="col-span-2 my-1" />

          <dt className="text-muted-foreground">Prepaid</dt>
          <dd className="font-mono">{fmtINR(recon?.prepaid)}</dd>

          <dt className="text-muted-foreground">Refunds received</dt>
          <dd className="font-mono">{fmtINR(recon?.refundsReceived)}</dd>

          <Separator className="col-span-2 my-1" />

          <dt className="font-medium">Balance (prepaid − delivered − refunds)</dt>
          <dd
            className={cn(
              "font-mono font-semibold",
              (recon?.balance ?? 0) > 0 ? "text-amber-600" : "text-green-700",
            )}
          >
            {fmtINR(recon?.balance)}
          </dd>
        </dl>
      </section>
        );
      })()}

      {/* ── Status-driven action buttons ───────────────────────────────── */}
      {!isReadOnly && (
        <div className="flex flex-wrap gap-3 pb-4">
          {order.status === StockInOrderStatus.Fully_Delivered && (
            <Button
              onClick={() => setShowCloseDialog(true)}
              disabled={closeMutPending}
            >
              {closeMutPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <PackageCheck className="mr-1.5 h-4 w-4" />
              Close Order
            </Button>
          )}

          {(order.status === StockInOrderStatus.Placed ||
            order.status === StockInOrderStatus.Partially_Delivered) && (
            <>
              <Button onClick={() => setShowDeliveryForm(true)}>
                <PackageCheck className="mr-1.5 h-4 w-4" />
                Record Delivery
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowCancelDialog(true)}
                disabled={cancelMutPending}
              >
                {cancelMutPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <PackageX className="mr-1.5 h-4 w-4" />
                Cancel Order
              </Button>
            </>
          )}
        </div>
      )}

      {/* ── Inline delivery form ────────────────────────────────────────── */}
      {showDeliveryForm && (
        <div className="rounded-md border p-4">
          <StockInOrderDeliveryForm
            order={order}
            onSaved={() => setShowDeliveryForm(false)}
            onDiscard={() => setShowDeliveryForm(false)}
          />
        </div>
      )}
    </div>
  );
}

// ─── Payment editor (Draft Record Payment + Edit Payment Details) ────────────

interface PaymentFieldsEditorProps {
  paidBy: string;
  setPaidBy: (v: string) => void;
  paymentMethod: string;
  setPaymentMethod: (v: string) => void;
  paymentAccountDetail: string;
  setPaymentAccountDetail: (v: string) => void;
  paymentReference: string;
  setPaymentReference: (v: string) => void;
  paymentDate: string;
  setPaymentDate: (v: string) => void;
  paymentProofUrl: string | null;
  setPaymentProofUrl: (v: string | null) => void;
}

function PaymentFieldsEditor(props: PaymentFieldsEditorProps) {
  const {
    paidBy, setPaidBy,
    paymentMethod, setPaymentMethod,
    paymentAccountDetail, setPaymentAccountDetail,
    paymentReference, setPaymentReference,
    paymentDate, setPaymentDate,
    paymentProofUrl, setPaymentProofUrl,
  } = props;
  const accountDetail = accountDetailLabel(paymentMethod);
  const referenceRequired = paymentMethod && paymentMethod !== "Cash";

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium">Paid by</label>
          <select
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={paidBy}
            onChange={(e) => setPaidBy(e.target.value)}
          >
            {PAID_BY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">Payment method</label>
          <select
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={paymentMethod}
            onChange={(e) => {
              setPaymentMethod(e.target.value);
              // Clear method-specific sub-field when the method changes — keeps
              // operator from saving a stale "HDFC ****1234" against UPI.
              setPaymentAccountDetail("");
            }}
          >
            <option value="">— select —</option>
            {PAYMENT_METHOD_OPTIONS.map((m) => (
              <option key={m} value={m}>{m.replace(/_/g, " ")}</option>
            ))}
          </select>
        </div>
      </div>

      {accountDetail && paymentMethod !== "Wallet" && (
        <div className="space-y-1">
          <label className="text-xs font-medium">{accountDetail.label}</label>
          <Input
            placeholder={accountDetail.placeholder}
            value={paymentAccountDetail}
            onChange={(e) => setPaymentAccountDetail(e.target.value)}
            className="h-9"
          />
        </div>
      )}
      {paymentMethod === "Wallet" && (
        <div className="space-y-1">
          <label className="text-xs font-medium">Wallet provider</label>
          <select
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={paymentAccountDetail}
            onChange={(e) => setPaymentAccountDetail(e.target.value)}
          >
            <option value="">— select —</option>
            {WALLET_PROVIDERS.map((w) => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium">
            Reference / UTR{referenceRequired ? "" : " (optional)"}
          </label>
          <Input
            placeholder="UTR, txn id, cheque no"
            value={paymentReference}
            onChange={(e) => setPaymentReference(e.target.value)}
            className="h-9"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">Payment date</label>
          <Input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="h-9"
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium">Payment proof (photo / screenshot)</label>
        <PaymentProofField value={paymentProofUrl} onChange={setPaymentProofUrl} />
      </div>
    </div>
  );
}

// Inline upload + preview tile for payment proof. Mirrors the FileField used
// in expenses.tsx so behavior matches across the app.
function PaymentProofField({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  async function handle(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r: UploadResult = await uploadPaymentProof(f);
      if (r.ok) { onChange(r.url); toast({ title: "Payment proof uploaded." }); }
      else toast({ title: r.message, variant: "destructive" });
    } finally { setUploading(false); e.target.value = ""; }
  }
  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm underline truncate flex-1">
          View payment proof
        </a>
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }
  return (
    <label className={`flex cursor-pointer items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm hover:bg-muted/50 ${uploading ? "pointer-events-none opacity-60" : ""}`}>
      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      <span>{uploading ? "Uploading…" : "Upload payment proof"}</span>
      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*" className="sr-only" onChange={handle} disabled={uploading} />
    </label>
  );
}
